import React from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { Box, Typography } from "@mui/material";
import { androidTabBand } from "../styles/androidJigsawTokens";

/** Matches android-jigsaw-muse AppShell tabs (text-only). */
export const APPLE_BOTTOM_TABS = [
    { id: "home", label: "Home" },
    { id: "library", label: "Gallery" },
    { id: "packs", label: "Premium packs" },
    { id: "settings", label: "Settings" },
];

/** Translucent puzzle glyphs drawn on the band (AppShell `MenuPieceLayer`). */
const MENU_PIECES = [
    { outline: false, top: -10, left: -8, size: 48, rotate: -18, opacity: 0.38 },
    { outline: true, top: 10, left: "18%", size: 28, rotate: 14, opacity: 0.46 },
    { outline: false, top: -16, left: "42%", size: 42, rotate: 8, opacity: 0.3 },
    { outline: true, top: 4, right: "16%", size: 32, rotate: -14, opacity: 0.42 },
    { outline: false, top: -6, right: -12, size: 50, rotate: 20, opacity: 0.34 },
];

function MenuPieceLayer() {
    return (
        <Box aria-hidden sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            {MENU_PIECES.map((piece, index) => {
                const Icon = piece.outline ? ExtensionOutlinedIcon : ExtensionIcon;
                return (
                    <Box
                        key={index}
                        sx={{
                            position: "absolute",
                            top: piece.top,
                            left: piece.left,
                            right: piece.right,
                            lineHeight: 0,
                            color: "rgba(255, 255, 255, 0.55)",
                            opacity: piece.opacity,
                        }}
                    >
                        <Icon sx={{ display: "block", fontSize: piece.size, transform: `rotate(${piece.rotate}deg)` }} />
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Bottom tab bar for App Store jigsaw shots.
 * Light theme (default) = Android AppShell: coloured band of the active tab + white labels.
 * @param {"home"|"library"|"packs"|"settings"} activeTab
 * @param {boolean} [dark=false] Legacy dark chrome (no live scene uses it).
 */
export default function AppleBottomNav({
    activeTab = "home",
    dark = false,
    navBg,
    navBorder,
    activeColor,
    inactiveColor,
}) {
    const bg = navBg ?? (dark ? "rgba(2, 24, 18, 0.78)" : (androidTabBand[activeTab] ?? androidTabBand.home));
    const border = navBorder ?? (dark ? "rgba(110, 231, 183, 0.16)" : "rgba(255, 255, 255, 0.28)");
    const on = activeColor ?? (dark ? "#a7f3d0" : "#ffffff");
    const off = inactiveColor ?? (dark ? "rgba(236, 253, 245, 0.72)" : "rgba(255, 255, 255, 0.78)");

    return (
        <Box
            sx={{
                position: "relative",
                flexShrink: 0,
                display: "flex",
                alignItems: "stretch",
                justifyContent: "space-around",
                // Keep label baselines inside the capture frame (iPad scale can clip overflow:hidden).
                overflow: "visible",
                bgcolor: bg,
                backdropFilter: dark ? "blur(18px) saturate(140%)" : "none",
                borderTop: `1px solid ${border}`,
                pt: "6px",
                pb: "max(10px, env(safe-area-inset-bottom, 0px))",
                zIndex: 8,
            }}
            role="tablist"
            aria-label="App tabs"
        >
            {dark ? null : <MenuPieceLayer />}
            {APPLE_BOTTOM_TABS.map((tab) => {
                const active = tab.id === activeTab;
                return (
                    <Box
                        key={tab.id}
                        role="tab"
                        aria-selected={active}
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 52,
                            px: 0.25,
                            color: active ? on : off,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily:
                                    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                fontSize: "0.8rem",
                                fontWeight: active ? 800 : 700,
                                lineHeight: 1.3,
                                textAlign: "center",
                                color: "inherit",
                                whiteSpace: "pre-line",
                                textShadow: dark ? "none" : "0 1px 2px rgba(15, 23, 42, 0.28)",
                            }}
                        >
                            {tab.label.replace(" ", "\n")}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}
